/*
 * cursorContext (Client-Seite) — baut den Block, den der Server vor jeder
 * User-Message als XML in den Anthropic-User-Turn schiebt (Spec §5).
 *
 * Form:
 *   {
 *     pane: 'main' | 'background' | 'outline' | 'context' | 'core',
 *     paragraphId: 'uuid' | null,
 *     paragraphIndex: number | null,       // 0-basierte Position innerhalb der
 *                                          // umgebenden Section (passt direkt
 *                                          // zu read_section's from_index/to_index)
 *     separatorId: 'uuid' | null,          // separator, der die umgebende Section eroeffnet
 *     isSeparatorCursor: boolean,          // cursor steht selbst auf einem separator
 *     selection: {                         // null wenn keine Selection oder kollabiert
 *       anchorParagraphId, focusParagraphId,
 *       anchorOffset, focusOffset,
 *       selectedText,                      // bis SELECTION_SNIPPET_MAX Zeichen
 *       truncated                          // true falls Snippet abgeschnitten
 *     } | null
 *   }
 *
 * Pane-Mapping: Wir lesen `layout.getActiveEditorPane()` — das liefert IMMER
 * einen aus OUTLINE/MAIN/CONTEXT, NIE CORE. Grund: Der User tippt seine Frage
 * in der Console (CORE), aber semantisch interessiert den Agent, wo der User
 * im Editor zuletzt war (das ist der Editor-Cursor, nicht der Console-Cursor).
 * Bei Console-Fokus gibt activePane CORE zurueck, getActiveEditorPane bleibt
 * beim zuletzt aktiven Editor-Pane stehen.
 *
 * Wenn der Cursor in einem Background-Paragraph steht (egal welches Pane),
 * liefern wir pane='background' — das ist die User-mentale Sicht "ich arbeite
 * am Background", auch wenn der Background-Inhalt rein technisch im MAIN-Pane
 * gerendert wird. Der System-Prompt deutet pane='background' entsprechend.
 */

import { PaneId, EDITOR, IMPLICIT_HEAD_ID } from '../config.js'

const SELECTION_SNIPPET_MAX = 1000

/**
 * Baut den cursorContext-Block fuer den AgentService.
 *
 * @param {object} args
 * @param {EditorModel} args.model
 * @param {Layout} args.layout
 * @returns {object|null}
 */
export function buildCursorContext({ model, layout }) {
    if (!model || !layout) return null
    const cursor = model.cursor
    if (!cursor) return null

    const paragraphId = cursor.paragraphId || null
    const para = paragraphId ? model.getParagraph(paragraphId) : null
    const isSeparatorCursor = !!para && para.type === 'separator'

    const inBg = paragraphId ? isInBackground(model, paragraphId) : false
    // getActiveEditorPane statt activePane: liefert immer OUTLINE/MAIN/CONTEXT,
    // nie CORE — der User tippt zwar in der Core-Console, aber der Agent will
    // wissen, wo der EDITOR-Cursor steht.
    const editorPane = typeof layout.getActiveEditorPane === 'function'
        ? layout.getActiveEditorPane()
        : layout.activePane
    const pane = mapPane(editorPane, inBg)
    const enclosingSepId = paragraphId ? findEnclosingSeparatorId(model, paragraphId) : null
    const paragraphIndex = paragraphId
        ? findParagraphIndexInSection(model, paragraphId, enclosingSepId, isSeparatorCursor)
        : null
    // Implicit-head Section: Cursor sitzt in einem Main-Paragraph vor jedem
    // Separator → kein enclosingSeparatorId. Statt das Attribut wegzulassen
    // (Server-Serializer ist dann blind), den Token reinspritzen, damit der
    // Agent seine Tools dagegen richten kann.
    const separatorId = (enclosingSepId === null && paragraphId && !inBg && !isSeparatorCursor)
        ? IMPLICIT_HEAD_ID
        : enclosingSepId
    const selection = buildSelection(model)

    return {
        pane,
        paragraphId,
        paragraphIndex,
        separatorId,
        isSeparatorCursor,
        selection
    }
}

/**
 * Liefert die 0-basierte Position des Paragraphen innerhalb der Section,
 * die er bewohnt. Eine Section ist "Separator + alle Paragraphen bis zum
 * naechsten Separator (exklusiv)".
 *
 * - Bei `isSeparatorCursor`: paragraphIndex ist null — der Cursor sitzt auf
 *   dem Separator selbst, nicht in der Liste der Section-Paragraphen.
 * - Bei "implicit head"-Section (paragraphen vor dem ersten Separator):
 *   wir zaehlen einfach von 0 an in der pre-Separator-Liste.
 *
 * @returns {number|null}
 */
export function findParagraphIndexInSection(model, paragraphId, enclosingSeparatorId, isSeparatorCursor) {
    if (isSeparatorCursor) return null

    let inSection = enclosingSeparatorId === null   // implicit-head: gleich aktiv
    let idx = 0
    for (const p of model.paragraphs) {
        if (p.type === 'separator') {
            if (p.id === enclosingSeparatorId) {
                inSection = true
                idx = 0
                continue
            }
            // Naechster Separator beendet die Suche, falls wir bereits drin waren
            if (inSection) return null
            continue
        }
        if (!inSection) continue
        if (p.id === paragraphId) return idx
        idx++
    }
    return null
}

/**
 * Mappt Layout-PaneIds auf die im Server-Prompt erwarteten Werte (lowercase).
 * Erwartet OUTLINE/MAIN/CONTEXT (CORE kommt hier nicht an, weil wir
 * getActiveEditorPane lesen). Wenn der Cursor in Background liegt UND der
 * aktive Pane MAIN ist, mappen wir auf 'background' — das spiegelt die
 * User-mentale Sicht.
 */
function mapPane(editorPaneId, inBackground) {
    if (editorPaneId === PaneId.MAIN && inBackground) return 'background'
    switch (editorPaneId) {
        case PaneId.MAIN:    return 'main'
        case PaneId.OUTLINE: return 'outline'
        case PaneId.CONTEXT: return 'context'
        default:             return 'main'
    }
}

/**
 * Pruefe ob ein Paragraph im Background-Bereich liegt (nach dem ===-Separator).
 * Wir scannen die paragraphs-Liste, bis wir entweder den Background-Separator
 * oder den Ziel-Paragraph erreichen.
 */
export function isInBackground(model, paragraphId) {
    let inBg = false
    for (const p of model.paragraphs) {
        if (p.type === 'separator' && p.outlineDepth === EDITOR.BACKGROUND_SEPARATOR_DEPTH) {
            inBg = true
            // Background-Separator selbst zaehlt schon zum Background.
            if (p.id === paragraphId) return true
            continue
        }
        if (p.id === paragraphId) return inBg
    }
    return false
}

/**
 * Finde die separatorId, die die Section eroeffnet, in der der angegebene
 * Paragraph liegt. Wenn der Paragraph selbst ein Separator ist, gilt sein
 * eigener nicht — wir suchen den vorherigen.
 *
 * Liefert null wenn kein vorheriger Separator existiert (Cursor vor erstem
 * Separator).
 */
export function findEnclosingSeparatorId(model, paragraphId) {
    let lastSeparatorId = null
    for (const p of model.paragraphs) {
        if (p.id === paragraphId) {
            // Cursor selbst auf separator: der ist die enclosing section
            if (p.type === 'separator') return p.id
            return lastSeparatorId
        }
        if (p.type === 'separator') lastSeparatorId = p.id
    }
    return null
}

/**
 * Baut den selection-Subblock — null wenn keine Selection oder kollabiert.
 */
function buildSelection(model) {
    const sel = model.selection
    if (!sel || sel.isCollapsed()) return null

    const norm = sel.getNormalized(model)
    const anchorParagraphId = norm.start.paragraphId
    const focusParagraphId = norm.end.paragraphId
    const anchorOffset = norm.start.offset
    const focusOffset = norm.end.offset

    // Snippet sammeln: text zwischen start und end. Wenn > SELECTION_SNIPPET_MAX,
    // truncated=true und KEIN snippet.
    const snippet = collectSnippet(model, norm.start, norm.end)
    if (snippet === null) {
        return {
            anchorParagraphId, focusParagraphId,
            anchorOffset, focusOffset,
            truncated: true
        }
    }
    return {
        anchorParagraphId, focusParagraphId,
        anchorOffset, focusOffset,
        selectedText: snippet,
        truncated: false
    }
}

/**
 * Sammelt den selektierten Text. Wenn er > SELECTION_SNIPPET_MAX waere,
 * gibt null zurueck (Caller setzt truncated=true).
 */
function collectSnippet(model, start, end) {
    const startIdx = model.getParagraphIndex(start.paragraphId)
    const endIdx = model.getParagraphIndex(end.paragraphId)
    if (startIdx < 0 || endIdx < 0) return ''

    if (startIdx === endIdx) {
        const p = model.paragraphs[startIdx]
        const text = p?.text || ''
        const slice = text.slice(start.offset, end.offset)
        return slice.length <= SELECTION_SNIPPET_MAX ? slice : null
    }

    let total = 0
    const parts = []
    for (let i = startIdx; i <= endIdx; i++) {
        const p = model.paragraphs[i]
        if (!p) continue
        const text = p.text || ''
        let slice
        if (i === startIdx) slice = text.slice(start.offset)
        else if (i === endIdx) slice = text.slice(0, end.offset)
        else slice = text
        parts.push(slice)
        total += slice.length + 1   // +1 fuer Trenner
        if (total > SELECTION_SNIPPET_MAX) return null
    }
    return parts.join('\n')
}

export const _internal = { SELECTION_SNIPPET_MAX, mapPane, collectSnippet }
